import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, KeyRound, AlertCircle, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Token de réinitialisation manquant.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: password
      });
      toast.success("Mot de passe mis à jour !");
      navigate("/login");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Une erreur est survenue. Le lien a peut-être expiré.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8">
        <Card className="p-12 max-w-lg w-full text-center shadow-xl rounded-lg">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
          <h1 className="text-3xl font-black text-primary mb-4">Lien invalide</h1>
          <p className="text-lg text-foreground/60 mb-8">
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          <Button onClick={() => navigate("/forgot-password")} className="w-full h-14 text-lg">
            Demander un nouveau lien
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8" data-testid="reset-password-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-12">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-foreground/40 hover:text-primary transition-colors font-semibold uppercase tracking-widest text-xs"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <span className="text-2xl font-bold text-primary">Sequenti</span>
        </div>

        <Card className="p-12 border-none bg-white shadow-xl rounded-lg border border-border/40 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-accent" />
          
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/5 rounded-full mb-6">
              <KeyRound className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl font-black text-primary mb-3">
              Nouveau mot de passe
            </h1>
            <p className="text-lg text-foreground/40 font-medium">
              Choisissez un mot de passe sécurisé
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-destructive/5 text-destructive rounded-lg border border-destructive/10 text-base font-semibold">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div className="space-y-3">
              <Label htmlFor="password" className="text-base font-bold text-primary">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="h-14 text-lg rounded-lg border-border/50 pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-primary"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="confirmPassword" className="text-base font-bold text-primary">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                type="password"
                className="h-14 text-lg rounded-lg border-border/50"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-white hover:bg-primary/90 text-xl h-16 rounded-lg shadow-lg transition-all active:scale-95 mt-4"
              disabled={loading}
              data-testid="reset-submit-button"
            >
              {loading ? "Mise à jour..." : "Réinitialiser le mot de passe"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
